import React from 'react';
import { RefreshCw, Trash2, Plus, Save, Play, Info, Link2, Flame, Activity, Dumbbell, Layers } from 'lucide-react';

const SCHEME_PRESETS = ['21-15-9', '15-12-9', '12-10-8-6', '10-8-6-4', '5-5-5-5-5'];

export default function BuilderStepThree({
    glassClass, title, setTitle, category, timeCap, setTimeCap, mode, generateAITemplate,
    wodFormat, setWodFormat, exercises, expandedEx, setExpandedEx, updateEx, removeEx,
    setShowPicker, handleSave, handleStart, computeDisplayCodes
}) {
    const codes = typeof computeDisplayCodes === 'function'
        ? computeDisplayCodes(exercises)
        : exercises.map((_, i) => String.fromCharCode(65 + i));

    const getSummaryLine = (ex) => {
        if (ex.isCardioBlock || category === 'Cardio') {
            const roundsPrefix = ex.sets && parseInt(ex.sets, 10) > 1 ? `${ex.sets}x ` : '';
            return `${roundsPrefix}${ex.reps || 10} ${ex.metric || 'Min'}${ex.weight ? ` @ ${ex.weight}` : ''}`;
        }
        if (ex.repMode === 'failure' || ex.isFailure) {
            return `${ex.sets || 3} Sets x 🔥 Tot Failure${ex.weight ? ` @ ${ex.weight}kg` : ''}`;
        }
        if (ex.repMode === 'scheme') {
            const schemeParts = String(ex.reps || '21-15-9').split('-').filter(Boolean);
            return `Schema: ${ex.reps || '21-15-9'} Reps (${schemeParts.length} sets)${ex.weight ? ` @ ${ex.weight}kg` : ''}`;
        }
        if (category === 'Fitness') {
            return `${ex.sets || 3} Sets x ${ex.reps || 10} ${ex.metric || 'Reps'}${ex.weight ? ` @ ${ex.weight}kg` : ''}`;
        }
        return `${ex.reps || 10} ${ex.metric || 'Reps'}${ex.weight ? ` @ ${ex.weight}` : ''}`;
    };

    const handleModeSwitch = (ex, newMode) => {
        if (newMode === 'cardio') {
            updateEx(ex.id, {
                isCardioBlock: true,
                repMode: 'cardio',
                sets: 1,
                reps: ex.metric === 'Meters' ? '1000' : (ex.metric === 'Cals' ? '20' : '15'),
                metric: ['Min', 'Sec', 'Cals', 'Meters', 'KM'].includes(ex.metric) ? ex.metric : 'Min'
            });
        } else if (newMode === 'failure') {
            updateEx(ex.id, {
                isCardioBlock: false,
                repMode: 'failure',
                isFailure: true,
                sets: ex.sets || 3,
                reps: 'Failure',
                metric: 'Reps'
            });
        } else if (newMode === 'scheme') {
            const defaultScheme = String(ex.reps || '').includes('-') ? ex.reps : '21-15-9';
            const parts = defaultScheme.split('-').filter(Boolean);
            updateEx(ex.id, {
                isCardioBlock: false,
                repMode: 'scheme',
                isFailure: false,
                reps: defaultScheme,
                sets: parts.length || 3,
                metric: 'Reps'
            });
        } else {
            updateEx(ex.id, {
                isCardioBlock: false,
                repMode: 'standard',
                isFailure: false,
                sets: ex.sets || 3,
                reps: String(ex.reps || '').includes('-') || ex.reps === 'Failure' ? '10' : (ex.reps || '10'),
                metric: 'Reps'
            });
        }
    };

    return (
        <div className={`${glassClass} space-y-6`}>
            {/* Bovenste Sessie-Instellingen Balk */}
            <div className="bg-zinc-800/50 p-5 sm:p-6 rounded-2xl border border-zinc-700 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                    <label className="text-[11px] text-gray-400 font-black uppercase tracking-widest block mb-2">Titel Sessie</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Bijv. Upper Body & Conditioning"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-3.5 text-white font-bold outline-none focus:border-orange-500 transition-colors"
                    />
                </div>

                {category === 'CrossFit' ? (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] text-gray-400 font-black uppercase tracking-widest block mb-2">Format</label>
                            <select
                                value={wodFormat}
                                onChange={(e) => setWodFormat(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-3.5 text-white font-bold outline-none focus:border-orange-500 transition-colors"
                            >
                                <option value="AMRAP">AMRAP</option>
                                <option value="For Time">For Time</option>
                                <option value="EMOM">EMOM</option>
                                <option value="Tabata">Tabata</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] text-gray-400 font-black uppercase tracking-widest block mb-2">Timecap (Min)</label>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    placeholder="Geen"
                                    value={timeCap}
                                    onChange={(e) => setTimeCap(e.target.value)}
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-3.5 text-white font-bold outline-none focus:border-orange-500 transition-colors"
                                />
                                {mode === 'AI' && (
                                    <button
                                        type="button"
                                        onClick={() => generateAITemplate(category, parseInt(timeCap, 10))}
                                        className="bg-orange-600 hover:bg-orange-500 text-white px-3.5 rounded-xl flex items-center justify-center transition-colors"
                                        title="Pas schema aan op tijd"
                                    >
                                        <RefreshCw size={18} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <label className="text-[11px] text-gray-400 font-black uppercase tracking-widest block mb-2">Geplande Tijd (Minuten, optioneel)</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                placeholder="Vrij (0 minuten)"
                                value={timeCap}
                                onChange={(e) => setTimeCap(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-3.5 text-white font-bold outline-none focus:border-orange-500 transition-colors"
                            />
                            {mode === 'AI' && (
                                <button
                                    type="button"
                                    onClick={() => generateAITemplate(category, parseInt(timeCap, 10))}
                                    className="bg-orange-600 hover:bg-orange-500 text-white px-4 rounded-xl flex items-center justify-center transition-colors shadow-md"
                                    title="Pas schema aan op tijd"
                                >
                                    <RefreshCw size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Lijst met Oefeningen */}
            <div className="space-y-3">
                {exercises.map((ex, idx) => {
                    const displayCode = codes[idx] || String.fromCharCode(65 + idx);
                    const isSupersetLinked = idx > 0 && !!ex.isSuperset;
                    const activeMode = ex.isCardioBlock ? 'cardio' : (ex.repMode || (ex.isFailure ? 'failure' : 'standard'));

                    return (
                        <React.Fragment key={ex.id}>
                            {isSupersetLinked && (
                                <div className="flex items-center justify-center -my-2 relative z-10">
                                    <span className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-0.5 rounded-full shadow flex items-center gap-1">
                                        <Link2 size={12}/> Gekoppeld als Superset ({codes[idx - 1]} + {displayCode})
                                    </span>
                                </div>
                            )}

                            <div className={`bg-zinc-900 rounded-2xl border transition-all shadow-md ${
                                isSupersetLinked ? 'border-blue-500/60 border-l-4 border-l-blue-500' :
                                ex.isCardioBlock ? 'border-cyan-500/50 border-l-4 border-l-cyan-500' :
                                expandedEx === ex.id ? 'border-orange-500 border-l-4 border-l-orange-500' : 'border-zinc-700 hover:border-zinc-500'
                            }`}>
                                <div
                                    onClick={() => setExpandedEx(expandedEx === ex.id ? null : ex.id)}
                                    className="p-4 sm:p-5 flex items-center justify-between cursor-pointer"
                                >
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm border shrink-0 ${
                                            isSupersetLinked ? 'bg-blue-500/15 text-blue-500 border-blue-500/40' :
                                            ex.isCardioBlock ? 'bg-cyan-500/15 text-cyan-500 border-cyan-500/40' :
                                            'bg-orange-500/15 text-orange-500 border-orange-500/40'
                                        }`}>
                                            {displayCode}
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="font-black text-white text-base sm:text-lg truncate">{ex.name}</h4>
                                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                                                {getSummaryLine(ex)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        {ex.isWarmup && <span className="hidden sm:inline-flex bg-orange-500/20 text-orange-500 border border-orange-500/30 text-[10px] font-black px-2.5 py-1 rounded uppercase">Warmup</span>}
                                        {isSupersetLinked && <span className="hidden sm:inline-flex bg-blue-500/20 text-blue-500 border border-blue-500/30 text-[10px] font-black px-2.5 py-1 rounded uppercase">Superset</span>}
                                        {ex.isDropset && <span className="hidden sm:inline-flex bg-red-500/20 text-red-500 border border-red-500/30 text-[10px] font-black px-2.5 py-1 rounded uppercase">Dropset</span>}
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); removeEx(ex.id); }}
                                            className="text-gray-400 hover:text-red-500 p-2 bg-zinc-800 rounded-lg border border-zinc-700 transition-colors"
                                            title="Verwijder oefening"
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </div>

                                {expandedEx === ex.id && (
                                    <div className="p-4 sm:p-5 pt-3 border-t border-zinc-800 space-y-5 animate-fade-in">
                                        {/* Modus Kiezer per Oefening */}
                                        <div>
                                            <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-2">Type Opbouw voor deze oefening</label>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleModeSwitch(ex, 'standard')}
                                                    className={`py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 border transition-all ${
                                                        activeMode === 'standard'
                                                            ? 'bg-orange-600 text-white border-orange-500'
                                                            : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                    }`}
                                                >
                                                    <Dumbbell size={14}/> Vaste Sets
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleModeSwitch(ex, 'scheme')}
                                                    className={`py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 border transition-all ${
                                                        activeMode === 'scheme'
                                                            ? 'bg-orange-600 text-white border-orange-500'
                                                            : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                    }`}
                                                >
                                                    <Layers size={14}/> 21-15-9 / Reeks
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleModeSwitch(ex, 'failure')}
                                                    className={`py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 border transition-all ${
                                                        activeMode === 'failure'
                                                            ? 'bg-purple-600 text-white border-purple-500'
                                                            : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                    }`}
                                                >
                                                    <Flame size={14}/> Tot Failure
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleModeSwitch(ex, 'cardio')}
                                                    className={`py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 border transition-all ${
                                                        activeMode === 'cardio'
                                                            ? 'bg-cyan-600 text-white border-cyan-500'
                                                            : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                    }`}
                                                >
                                                    <Activity size={14}/> Tijd / Cals / M
                                                </button>
                                            </div>
                                        </div>

                                        {/* Velden afhankelijk van gekozen modus */}
                                        {activeMode === 'cardio' ? (
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Doel Eenheid</label>
                                                    <select
                                                        value={ex.metric || 'Min'}
                                                        onChange={(e) => updateEx(ex.id, 'metric', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-cyan-500"
                                                    >
                                                        <option value="Min">Minuten (Tijd)</option>
                                                        <option value="Sec">Seconden</option>
                                                        <option value="Cals">Calorieën (Cals)</option>
                                                        <option value="Meters">Meters (Afstand)</option>
                                                        <option value="KM">Kilometers (KM)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">
                                                        Waarde ({ex.metric || 'Min'})
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={ex.reps}
                                                        onChange={(e) => updateEx(ex.id, 'reps', e.target.value)}
                                                        placeholder="Bijv. 15 of 1000"
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-cyan-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Rondes / Intervallen</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ex.sets || 1}
                                                        onChange={(e) => updateEx(ex.id, 'sets', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-cyan-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Weerstand / Pace</label>
                                                    <input
                                                        type="text"
                                                        value={ex.weight}
                                                        onChange={(e) => updateEx(ex.id, 'weight', e.target.value)}
                                                        placeholder="Bijv. Zone 2 / Stand 6"
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-cyan-500"
                                                    />
                                                </div>
                                            </div>
                                        ) : activeMode === 'scheme' ? (
                                            <div className="space-y-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Snelkiezers:</span>
                                                    {SCHEME_PRESETS.map(preset => (
                                                        <button
                                                            key={preset}
                                                            type="button"
                                                            onClick={() => updateEx(ex.id, { reps: preset, sets: preset.split('-').length })}
                                                            className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                                                                ex.reps === preset
                                                                    ? 'bg-orange-600 text-white border-orange-500'
                                                                    : 'bg-zinc-800 text-gray-300 border-zinc-700 hover:border-orange-500'
                                                            }`}
                                                        >
                                                            {preset}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div>
                                                        <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Reps per set (gescheiden met -)</label>
                                                        <input
                                                            type="text"
                                                            value={ex.reps}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                const count = val.split('-').filter(s => s.trim() !== '').length;
                                                                updateEx(ex.id, { reps: val, sets: Math.max(1, count) });
                                                            }}
                                                            placeholder="Bijv. 21-15-9 of 12-10-8-6"
                                                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Startgewicht (kg)</label>
                                                        <input
                                                            type="text"
                                                            value={ex.weight}
                                                            onChange={(e) => updateEx(ex.id, 'weight', e.target.value)}
                                                            placeholder="Optioneel"
                                                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Rust tussen sets (sec)</label>
                                                        <input
                                                            type="number"
                                                            value={ex.rest}
                                                            onChange={(e) => updateEx(ex.id, 'rest', e.target.value)}
                                                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : activeMode === 'failure' ? (
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Aantal Sets (Tot Failure)</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ex.sets}
                                                        onChange={(e) => updateEx(ex.id, 'sets', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-purple-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Gewicht (kg / BW)</label>
                                                    <input
                                                        type="text"
                                                        value={ex.weight}
                                                        onChange={(e) => updateEx(ex.id, 'weight', e.target.value)}
                                                        placeholder="Bijv. BW of 40"
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-purple-500"
                                                    />
                                                </div>
                                                <div className="col-span-2 sm:col-span-1">
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Rust (Sec)</label>
                                                    <input
                                                        type="number"
                                                        value={ex.rest}
                                                        onChange={(e) => updateEx(ex.id, 'rest', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-purple-500"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Sets</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={ex.sets}
                                                        onChange={(e) => updateEx(ex.id, 'sets', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Reps</label>
                                                    <input
                                                        type="text"
                                                        value={ex.reps}
                                                        onChange={(e) => updateEx(ex.id, 'reps', e.target.value)}
                                                        placeholder="Bijv. 10 of 8-12"
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Gewicht (kg)</label>
                                                    <input
                                                        type="text"
                                                        value={ex.weight}
                                                        onChange={(e) => updateEx(ex.id, 'weight', e.target.value)}
                                                        placeholder="Optioneel"
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1.5">Rust (Sec)</label>
                                                    <input
                                                        type="number"
                                                        value={ex.rest}
                                                        onChange={(e) => updateEx(ex.id, 'rest', e.target.value)}
                                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Extra Opties (Tempo, RPE, Warmup, Superset, Dropset) */}
                                        {!ex.isCardioBlock && category === 'Fitness' && (
                                            <>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-zinc-800">
                                                    <div>
                                                        <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                                            <Info size={12} className="text-blue-500"/> Tempo
                                                        </label>
                                                        <select
                                                            value={ex.tempo}
                                                            onChange={(e) => updateEx(ex.id, 'tempo', e.target.value)}
                                                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                        >
                                                            <option value="-">Standaard / Geen Tempo</option>
                                                            <option value="2010">2010 (Gecontroleerd neer)</option>
                                                            <option value="3110">3110 (Pauze onderin)</option>
                                                            <option value="4020">4020 (Time under Tension)</option>
                                                            <option value="10X0">10X0 (Explosief omhoog)</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                                            <Info size={12} className="text-red-500"/> Intensiteit (RPE)
                                                        </label>
                                                        <select
                                                            value={ex.rpe}
                                                            onChange={(e) => updateEx(ex.id, 'rpe', e.target.value)}
                                                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-orange-500"
                                                        >
                                                            <option value="-">Standaard / Geen RPE</option>
                                                            <option value="6">RPE 6 (Warm-up gewicht)</option>
                                                            <option value="7">RPE 7 (Ruim over in de tank)</option>
                                                            <option value="8">RPE 8 (Nog 2 reps over)</option>
                                                            <option value="9">RPE 9 (Nog 1 rep over)</option>
                                                            <option value="10">RPE 10 (Max effort / Failure)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap gap-2.5 pt-3 border-t border-zinc-800">
                                                    <button
                                                        type="button"
                                                        onClick={() => updateEx(ex.id, 'isWarmup', !ex.isWarmup)}
                                                        className={`px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all border ${
                                                            ex.isWarmup
                                                                ? 'bg-orange-600 text-white border-orange-500 shadow-sm'
                                                                : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                        }`}
                                                    >
                                                        🔥 Warm-up Sets
                                                    </button>

                                                    {idx > 0 ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => updateEx(ex.id, 'isSuperset', !ex.isSuperset)}
                                                            className={`px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all border ${
                                                                ex.isSuperset
                                                                    ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                                                                    : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                            }`}
                                                        >
                                                            🔗 {ex.isSuperset ? `Gekoppeld met ${exercises[idx - 1]?.name}` : `Koppel als Superset met vorige`}
                                                        </button>
                                                    ) : (
                                                        <span className="px-3.5 py-2 rounded-xl text-[11px] font-bold text-gray-500 bg-zinc-800/40 border border-zinc-800">
                                                            🔗 Voeg een 2e oefening toe om als Superset te koppelen
                                                        </span>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={() => updateEx(ex.id, 'isDropset', !ex.isDropset)}
                                                        className={`px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all border ${
                                                            ex.isDropset
                                                                ? 'bg-red-600 text-white border-red-500 shadow-sm'
                                                                : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:text-white'
                                                        }`}
                                                    >
                                                        📉 + Dropset
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}

                <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    className="w-full py-5 border-2 border-dashed border-zinc-600 hover:border-orange-500 hover:bg-orange-500/5 rounded-2xl flex items-center justify-center gap-3 text-gray-300 hover:text-orange-500 transition-all font-black uppercase tracking-wider text-sm group"
                >
                    <Plus size={22} className="group-hover:scale-125 transition-transform text-orange-500" /> Oefening of Cardio Toevoegen
                </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-6 mt-6 border-t border-zinc-700">
                <button
                    type="button"
                    onClick={handleSave}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-4 rounded-xl font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2 border border-zinc-600 shadow-md"
                >
                    <Save size={20}/> Opslaan
                </button>
                <button
                    type="button"
                    onClick={handleStart}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white py-4 rounded-xl font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2"
                >
                    <Play size={20} fill="currentColor"/> Start Sessie
                </button>
            </div>
        </div>
    );
}
